import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SwollenLegsHeroSection } from '../components/SwollenLegsHeroSection';
// Lazy load below-fold sections for faster initial paint
const DoctorBioSection = lazy(() => import('../components/DoctorBioSection').then(m => ({
  default: m.DoctorBioSection
})));
const ProblemSection = lazy(() => import('../components/ProblemSection').then(m => ({
  default: m.ProblemSection
})));
const SolutionSection = lazy(() => import('../components/SolutionSection').then(m => ({
  default: m.SolutionSection
})));
const MechanismSection = lazy(() => import('../components/MechanismSection').then(m => ({
  default: m.MechanismSection
})));
const BenefitsSection = lazy(() => import('../components/BenefitsSection').then(m => ({
  default: m.BenefitsSection
})));
const UseCasesSection = lazy(() => import('../components/UseCasesSection').then(m => ({
  default: m.UseCasesSection
})));
const CompareSection = lazy(() => import('../components/CompareSection').then(m => ({
  default: m.CompareSection
})));
const HowToUseSection = lazy(() => import('../components/HowToUseSection').then(m => ({
  default: m.HowToUseSection
})));
const TrustSection = lazy(() => import('../components/TrustSection').then(m => ({
  default: m.TrustSection
})));
const UGCCarousel = lazy(() => import('../components/UGCCarousel').then(m => ({
  default: m.UGCCarousel
})));
const BundleSection = lazy(() => import('../components/BundleSection').then(m => ({
  default: m.BundleSection
})));
const FAQSection = lazy(() => import('../components/FAQSection').then(m => ({
  default: m.FAQSection
})));
const CTASection = lazy(() => import('../components/CTASection').then(m => ({
  default: m.CTASection
})));
const PolicyModal = lazy(() => import('../components/PolicyModal').then(m => ({
  default: m.PolicyModal
})));
const SalesNotification = lazy(() => import('../components/SalesNotification').then(m => ({
  default: m.SalesNotification
})));
import { PageTracker } from '../components/PageTracker';
import { FloatingCTA } from '../components/FloatingCTA';
type PolicyType = 'privacy' | 'terms' | 'refund';
const navigationLinks = [{
  name: 'How It Works',
  href: '#how-it-works'
}, {
  name: 'Benefits',
  href: '#benefits'
}, {
  name: 'How To Use',
  href: '#how-to-use'
}, {
  name: 'Pricing',
  href: '#bundles'
}, {
  name: 'FAQ',
  href: '#faq'
}];
export function SwollenLegsPage() {
  const [policyModal, setPolicyModal] = useState<{
    isOpen: boolean;
    type: PolicyType;
  }>({
    isOpen: false,
    type: 'privacy'
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  // Handle scrolling to hash on mount and when hash changes
  useEffect(() => {
    if (location.hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          const yOffset = -80;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location.hash]);
  const openPolicy = (type: PolicyType) => {
    setPolicyModal({
      isOpen: true,
      type
    });
  };
  const closePolicy = () => {
    setPolicyModal({
      ...policyModal,
      isOpen: false
    });
  };
  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    // Small delay to allow mobile menu to close before scrolling
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        // Calculate the position accounting for fixed header
        const yOffset = -80;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({
          top: y,
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  return <main className="w-full min-h-screen bg-bone overflow-x-hidden" data-id="element-3417">
      <PageTracker data-id="element-3418" />
      {/* Header */}
      <header className="bg-white md:bg-white/95 md:backdrop-blur-sm border-b border-bone-dark fixed top-0 left-0 right-0 z-50 shadow-sm" data-id="element-3419">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-3420">
          <Link to="/" className="flex items-center" data-id="element-3421">
            <img src="https://cdn.magicpatterns.com/uploads/483EwVYaDBT86YSQWmyKfo/GINEXT_LOGO.png" alt="GINEXT" className="h-6 w-auto" loading="eager" data-id="element-3422" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6" data-id="element-3423">
            {navigationLinks.map(link => <a key={link.name} href={link.href} onClick={e => {
            e.preventDefault();
            handleNavClick(link.href);
          }} className="text-sm font-light text-charcoal hover:text-ginger transition-colors cursor-pointer" data-id="element-3424">
                {link.name}
              </a>)}
          </nav>

          {/* Mobile Menu Button */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-charcoal hover:text-ginger transition-colors" aria-label="Toggle menu" data-id="element-3425">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-3426" /> : <Menu className="w-6 h-6" data-id="element-3427" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence data-id="element-3428">
          {mobileMenuOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="md:hidden overflow-hidden border-t border-bone-dark bg-white" data-id="element-3429">
              <nav className="px-4 py-4 space-y-1" data-id="element-3430">
                {navigationLinks.map(link => <motion.div key={link.name} data-id="element-3431">
                    <a href={link.href} onClick={e => {
                e.preventDefault();
                setMobileMenuOpen(false);
                handleNavClick(link.href);
              }} className="block py-3 px-4 text-base font-light text-charcoal hover:text-ginger hover:bg-bone/50 rounded-lg transition-all cursor-pointer" data-id="element-3432">
                      {link.name}
                    </a>
                  </motion.div>)}
              </nav>
            </motion.div>}
        </AnimatePresence>
      </header>

      {/* Add padding to account for fixed header */}
      <div className="pt-[57px]" data-id="element-3433">
        <SwollenLegsHeroSection data-id="element-3434" />
        <Suspense fallback={<div className="min-h-[200px]" data-id="element-3436" />} data-id="element-3435">
          <DoctorBioSection data-id="element-3437" />
          <ProblemSection data-id="element-3438" />
          <SolutionSection data-id="element-3439" />
          <div id="how-it-works" className="scroll-mt-20" data-id="element-3440">
            <MechanismSection data-id="element-3441" />
          </div>
          <div id="benefits" className="scroll-mt-20" data-id="element-3442">
            <BenefitsSection beforeImage="https://cdn.magicpatterns.com/uploads/6CcWJy3w5feuNNFsbabayV/feets_.png" afterImage="https://cdn.magicpatterns.com/uploads/6aNW92ThfkGsG5cB4vdddM/after.png" data-id="element-3443" />
          </div>
          <CompareSection data-id="element-3444" />
          <UseCasesSection data-id="element-3445" />
          <div id="how-to-use" className="scroll-mt-20" data-id="element-3446">
            <HowToUseSection data-id="element-3447" />
          </div>
          <TrustSection data-id="element-3448" />
          <UGCCarousel data-id="element-3449" />
          <div id="bundles" className="scroll-mt-20" data-id="element-3450">
            <BundleSection data-id="element-3451" />
          </div>
          <div id="faq" className="scroll-mt-20" data-id="element-3452">
            <FAQSection data-id="element-3453" />
          </div>
          <CTASection data-id="element-3454" />
        </Suspense>
      </div>

      {/* Footer */}
      <footer className="bg-charcoal text-gray-400 py-4 text-center text-xs border-t border-white/10" data-id="element-3455">
        <div className="max-w-6xl mx-auto px-4" data-id="element-3456">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-3" data-id="element-3457">
            <button onClick={() => openPolicy('privacy')} className="hover:text-ginger transition-colors underline" data-id="element-3458">
              Privacy Policy
            </button>
            <button onClick={() => openPolicy('terms')} className="hover:text-ginger transition-colors underline" data-id="element-3459">
              Terms of Service
            </button>
            <button onClick={() => openPolicy('refund')} className="hover:text-ginger transition-colors underline" data-id="element-3460">
              Refund Policy
            </button>
          </div>
          <p data-id="element-3461">© {new Date().getFullYear()} GINEXT. All rights reserved.</p>
          <p className="mt-1" data-id="element-3462">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease.
          </p>
        </div>
      </footer>

      <Suspense fallback={null} data-id="element-3463">
        <PolicyModal isOpen={policyModal.isOpen} onClose={closePolicy} type={policyModal.type} data-id="element-3464" />
      </Suspense>

      {/* Sales Notification */}
      <Suspense fallback={null} data-id="element-3465">
        <SalesNotification data-id="element-3466" />
      </Suspense>

      {/* Floating CTA - Mobile & Desktop */}
      <FloatingCTA ctaText="Try GINEXT Risk-Free" ctaLink="#bundles" subText="30-Day Money Back Guarantee" data-id="element-3467" />
    </main>;
}